import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Download, ChevronLeft, CheckCircle, HelpCircle, Code, 
  MessageSquare, LayoutDashboard, Terminal, ThumbsUp, AlertCircle, XCircle,
  Video, Flag, Clock
} from 'lucide-react';
import { 
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, 
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip as RechartsTooltip, Legend 
} from 'recharts';
import api from '../api/axios';
import html2pdf from 'html2pdf.js';
import Swal from 'sweetalert2';

const customStyles = `
  @keyframes blob {
    0% { transform: translate(0px, 0px) scale(1); }
    33% { transform: translate(30px, -50px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.9); }
    100% { transform: translate(0px, 0px) scale(1); }
  }
  .animate-blob { animation: blob 15s infinite alternate ease-in-out; }
  
  .timeline-scrollbar::-webkit-scrollbar { width: 4px; }
  .timeline-scrollbar::-webkit-scrollbar-track { background: transparent; }
  .timeline-scrollbar::-webkit-scrollbar-thumb { background: rgba(99, 102, 241, 0.2); border-radius: 4px; }
  .timeline-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(99, 102, 241, 0.5); }
`;

const renderCustomizedLabel = ({ cx, cy, midAngle, outerRadius, subject }) => {
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 20; 
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  
  return (
    <text 
      x={x} y={y} 
      textAnchor={x > cx ? 'start' : 'end'} 
      dominantBaseline="central" 
      className="text-[12px] font-bold tracking-[0.1em] fill-slate-700 dark:fill-slate-300"
    >
      {subject}
    </text>
  );
};

const parseUserAnswer = (rawText) => {
  if (!rawText) return { language: 'N/A', code: '', output: '', explanation: '' };
  try {
    const langMatch = rawText.match(/LANGUAGE:\s*(.*?)\n/i);
    const codeMatch = rawText.match(/CODE:\n([\s\S]*?)(?=\n\nOUTPUT:|\nOUTPUT:|$)/i);
    const outputMatch = rawText.match(/OUTPUT:\n([\s\S]*?)(?=\n\nEXPLANATION:|\nEXPLANATION:|$)/i);
    const expMatch = rawText.match(/EXPLANATION:\n([\s\S]*)$/i);
    if (!langMatch && !codeMatch && !outputMatch && !expMatch) return { language: 'Unknown', code: rawText, output: '', explanation: '' };
    return { 
      language: langMatch ? langMatch[1].trim() : 'N/A', 
      code: codeMatch ? codeMatch[1].trim() : '', 
      output: outputMatch ? outputMatch[1].trim() : '', 
      explanation: expMatch ? expMatch[1].trim() : '' 
    };
  } catch (e) {
    return { language: 'Error', code: rawText, output: '', explanation: '' };
  }
};

const Report = () => {
  const { session_id } = useParams();
  const navigate = useNavigate();
  
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  const pdfRef = useRef();

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const res = await api.get(`/results/${session_id}/report`);
        setData(res.data);
      } catch (err) {
        console.error("Failed to load report", err);
        Swal.fire({
          icon: 'error',
          title: 'Failed to load report data',
          confirmButtonColor: '#4F46E5',
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchReport();
  }, [session_id]);

  const handleDownloadPDF = () => {
    if (!data) return;
    setIsGeneratingPDF(true);
    
    const element = pdfRef.current;
    const safeName = data.candidate_name ? data.candidate_name.replace(/\s+/g, '_') : 'Candidate';
    const filename = `${safeName}_Report.pdf`;

    const opt = {
      margin:       [0.5, 0.2, 0.5, 0.2], 
      filename:     filename,
      image:        { type: 'jpeg', quality: 1 },
      html2canvas:  { scale: 2, useCORS: true, logging: false, windowWidth: 800 },
      jsPDF:        { unit: 'in', format: 'a4', orientation: 'portrait' },
      pagebreak:    { mode: ['css', 'legacy'] } 
    };

    html2pdf().set(opt).from(element).save().then(() => {
      setIsGeneratingPDF(false);
    });
  };

  if (isLoading) {
    return (
      <div className="flex-1 h-[calc(100vh-5rem)] bg-slate-50 dark:bg-[#05050A] flex flex-col items-center justify-center transition-colors duration-500">
        <div className="w-8 h-8 border-2 border-indigo-600 dark:border-indigo-400 border-t-transparent rounded-full animate-spin mb-4"></div>
        <span className="text-slate-500 dark:text-slate-400 text-xs font-bold tracking-wide">Loading AI Report...</span>
      </div>
    );
  }

  const report = data.detailed_report;
  const scores = report.scores_and_summary;
  const hasCoding = report.coding_assessments && report.coding_assessments.length > 0;

  const chartData = [
    { subject: data.skill_names.skill1, score: scores.skill1_score, average: data.campaign_averages.skill1, fullMark: 10 },
    { subject: data.skill_names.skill2, score: scores.skill2_score, average: data.campaign_averages.skill2, fullMark: 10 },
    { subject: data.skill_names.skill3, score: scores.skill3_score, average: data.campaign_averages.skill3, fullMark: 10 },
    { subject: 'Communication', score: scores.communication_score, average: data.campaign_averages.communication, fullMark: 10 },
    { subject: 'Problem Solving', score: scores.problem_solving_score, average: data.campaign_averages.problem_solving, fullMark: 10 },
  ];

  const pieColors = ['#4f46e5', '#8b5cf6', '#10b981', '#f59e0b', '#f43f5e'];
  const videoUrl = `${api.defaults.baseURL || 'https://204.168.170.2.sslip.io/api'}/results/recordings/${session_id}.webm`;

  return (
    <div className="flex-1 h-[calc(100vh-5rem)] overflow-y-auto overflow-x-hidden bg-slate-50 dark:bg-[#05050A] text-slate-900 dark:text-slate-200 font-sans transition-colors duration-500 relative custom-scrollbar">
      <style>{customStyles}</style>

      {/* Ambient Glows */}
      <div className="fixed top-[-10%] left-[10%] w-[50vw] h-[50vh] rounded-full bg-indigo-200/40 dark:bg-indigo-600/10 blur-[120px] animate-blob z-0 pointer-events-none"></div>
      <div className="fixed bottom-[10%] right-[-10%] w-[50vw] h-[50vh] rounded-full bg-violet-200/40 dark:bg-violet-600/10 blur-[120px] animate-blob z-0 pointer-events-none" style={{ animationDelay: '2s' }}></div>

      <div className="px-6 lg:px-12 py-8 max-w-[1400px] mx-auto w-full relative z-10">
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium mb-6 transition-colors w-max text-sm tracking-wide"
        >
          <ChevronLeft size={18} /> Back to Results
        </button>

        {/* MAIN UI CANDIDATE PROFILE HEADER */}
        <div className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md p-8 rounded-[2rem] border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none mb-8">
          <div className="flex flex-col md:flex-row justify-between md:items-center gap-6 mb-8 pb-8 border-b border-slate-100 dark:border-white/10">
            <div className="flex items-center gap-5">
               <div className="w-16 h-16 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-500/20 rounded-full flex items-center justify-center text-2xl font-light uppercase shadow-sm dark:shadow-none">
                 {data.candidate_name.charAt(0)}
               </div>
               <div>
                 <h1 className="text-3xl font-semibold tracking-wide text-slate-900 dark:text-white flex items-center gap-4 mb-1">
                   {data.candidate_name}
                   {scores.hiring_decision && (
                       <span className={`px-3.5 py-1.5 text-[12px] font-bold rounded-lg tracking-wide border ${
                          scores.hiring_decision === 'Strong Hire' ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20' :
                          scores.hiring_decision === 'Hire' ? 'bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-400 border-green-200 dark:border-green-500/20' :
                          scores.hiring_decision === 'Hold' ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/20' :
                          'bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-500/20'
                      }`}>
                        {scores.hiring_decision}
                      </span>
                   )}
                 </h1>
                 <p className="text-slate-500 dark:text-slate-400 text-xs tracking-wide font-semibold">Session ID: <span className="font-mono text-[12px]">{data.session_id.split('-')[0]}...</span></p>
               </div>
            </div>
            
            <div className="flex gap-4">
              <button 
                onClick={handleDownloadPDF} 
                disabled={isGeneratingPDF}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-500 text-white rounded-xl font-bold tracking-wide transition-all shadow-md dark:shadow-[0_0_15px_rgba(99,102,241,0.3)] active:scale-95 text-[12px] disabled:opacity-50"
              >
                {isGeneratingPDF ? <span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></span> : <Download size={14} />}
                {isGeneratingPDF ? "Generating PDF..." : "Export to PDF"}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <div className="col-span-2 md:col-span-1 bg-indigo-50/50 dark:bg-indigo-500/5 p-5 rounded-2xl border border-indigo-100 dark:border-indigo-500/10 flex flex-col justify-center items-center text-center shadow-sm dark:shadow-none">
               <span className="text-[12px] font-bold text-indigo-600 dark:text-indigo-400 tracking-wide mb-1.5">Overall Score</span>
               <div className={`text-4xl font-light tracking-tighter ${data.overall_score >= 8 ? 'text-emerald-600 dark:text-emerald-400' : data.overall_score >= 5 ? 'text-amber-500 dark:text-amber-400' : 'text-rose-500 dark:text-rose-400'}`}>
                  {data.overall_score}<span className="text-base text-slate-400 dark:text-slate-500 font-medium">/10</span>
                </div>
            </div>
            
            <div className="bg-slate-50 dark:bg-[#111] p-5 rounded-2xl border border-slate-100 dark:border-white/5 flex flex-col justify-between">
               <span className="text-[12px] font-bold text-slate-500 dark:text-slate-400 tracking-wide line-clamp-1 mb-2" title={data.skill_names.skill1}>{data.skill_names.skill1}</span>
               <span className="text-2xl font-light text-slate-900 dark:text-white">{scores.skill1_score}<span className="text-sm text-slate-400">/10</span></span>
            </div>
            <div className="bg-slate-50 dark:bg-[#111] p-5 rounded-2xl border border-slate-100 dark:border-white/5 flex flex-col justify-between">
               <span className="text-[12px] font-bold text-slate-500 dark:text-slate-400 tracking-wide line-clamp-1 mb-2" title={data.skill_names.skill2}>{data.skill_names.skill2}</span>
               <span className="text-2xl font-light text-slate-900 dark:text-white">{scores.skill2_score}<span className="text-sm text-slate-400">/10</span></span>
            </div>
            <div className="bg-slate-50 dark:bg-[#111] p-5 rounded-2xl border border-slate-100 dark:border-white/5 flex flex-col justify-between">
               <span className="text-[12px] font-bold text-slate-500 dark:text-slate-400 tracking-wide line-clamp-1 mb-2" title={data.skill_names.skill3}>{data.skill_names.skill3}</span>
               <span className="text-2xl font-light text-slate-900 dark:text-white">{scores.skill3_score}<span className="text-sm text-slate-400">/10</span></span>
            </div>
            <div className="bg-slate-50 dark:bg-[#111] p-5 rounded-2xl border border-slate-100 dark:border-white/5 flex flex-col justify-between">
               <span className="text-[12px] font-bold text-slate-500 dark:text-slate-400 tracking-wide line-clamp-1 mb-2">Communication</span>
               <span className="text-2xl font-light text-slate-900 dark:text-white">{scores.communication_score}<span className="text-sm text-slate-400">/10</span></span>
            </div>
            <div className="bg-slate-50 dark:bg-[#111] p-5 rounded-2xl border border-slate-100 dark:border-white/5 flex flex-col justify-between">
               <span className="text-[12px] font-bold text-slate-500 dark:text-slate-400 tracking-wide line-clamp-1 mb-2">Problem Solving</span>
               <span className="text-2xl font-light text-slate-900 dark:text-white">{scores.problem_solving_score}<span className="text-sm text-slate-400">/10</span></span>
            </div>
          </div>
        </div>

        {/* TABS NAVIGATION */}
        <div className="flex border-b border-slate-200 dark:border-white/10 mb-8 overflow-x-auto shrink-0 custom-scrollbar pb-px">
          <button onClick={() => setActiveTab('overview')} className={`flex shrink-0 items-center gap-2 px-6 py-4 font-bold text-[12px] tracking-wide border-b-2 transition-colors ${activeTab === 'overview' ? 'border-indigo-600 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400' : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`}><LayoutDashboard size={14} strokeWidth={2}/> Overview</button>
          <button onClick={() => setActiveTab('questions')} className={`flex shrink-0 items-center gap-2 px-6 py-4 font-bold text-[12px] tracking-wide border-b-2 transition-colors ${activeTab === 'questions' ? 'border-indigo-600 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400' : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`}><MessageSquare size={14} strokeWidth={2}/> Q&A Breakdown</button>
          {hasCoding && <button onClick={() => setActiveTab('coding')} className={`flex shrink-0 items-center gap-2 px-6 py-4 font-bold text-[12px] tracking-wide border-b-2 transition-colors ${activeTab === 'coding' ? 'border-indigo-600 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400' : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`}><Code size={14} strokeWidth={2}/> Coding Assessment</button>}
          <button onClick={() => setActiveTab('video')} className={`flex shrink-0 items-center gap-2 px-6 py-4 font-bold text-[12px] tracking-wide border-b-2 transition-colors ${activeTab === 'video' ? 'border-indigo-600 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400' : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`}><Video size={14} strokeWidth={2}/> Video Analytics</button>
        </div>

        {/* TAB CONTENT */}
        {activeTab === 'overview' && (
          <div className="flex flex-col gap-8">
            {(scores.strengths || scores.weaknesses) ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md p-6 rounded-[2rem] shadow-sm dark:shadow-none border border-emerald-100 dark:border-emerald-500/20 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-1.5 h-full bg-emerald-500"></div>
                  <h3 className="font-medium dark:font-medium tracking-wide text-emerald-800 dark:text-emerald-400 text-lg mb-4 flex items-center gap-2"><ThumbsUp size={20} strokeWidth={1.5}/> Key Strengths</h3>
                  {scores.strengths?.length > 0 ? (
                      <ul className="space-y-3">
                        {scores.strengths.map((point, idx) => (
                          <li key={idx} className="flex gap-3 text-slate-700 dark:text-slate-300 text-sm font-light leading-relaxed">
                            <CheckCircle size={16} className="text-emerald-500 shrink-0 mt-0.5" strokeWidth={1.5} />
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                  ) : <p className="text-slate-500 dark:text-slate-400 text-sm italic font-light">No notable strengths identified.</p>}
                </div>

                <div className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md p-6 rounded-[2rem] shadow-sm dark:shadow-none border border-rose-100 dark:border-rose-500/20 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-1.5 h-full bg-rose-500"></div>
                  <h3 className="font-medium dark:font-medium tracking-wide text-rose-800 dark:text-rose-400 text-lg mb-4 flex items-center gap-2"><AlertCircle size={20} strokeWidth={1.5}/> Areas for Improvement</h3>
                  {scores.weaknesses?.length > 0 ? (
                      <ul className="space-y-3">
                        {scores.weaknesses.map((point, idx) => (
                          <li key={idx} className="flex gap-3 text-slate-700 dark:text-slate-300 text-sm font-light leading-relaxed">
                            <XCircle size={16} className="text-rose-500 shrink-0 mt-0.5" strokeWidth={1.5} />
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                  ) : <p className="text-slate-500 dark:text-slate-400 text-sm italic font-light">No notable weaknesses identified.</p>}
                </div>
              </div>
            ) : (
              <div className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md p-6 rounded-[2rem] shadow-sm dark:shadow-none border border-slate-200 dark:border-white/10">
                <h3 className="font-medium dark:font-medium tracking-wide text-slate-900 dark:text-white text-lg mb-3 flex items-center gap-2">AI Summary</h3>
                <p className="text-slate-600 dark:text-slate-300 font-light leading-relaxed text-sm">{scores.overall_description}</p>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md p-8 rounded-[2rem] shadow-sm dark:shadow-none border border-slate-200 dark:border-white/10 flex flex-col items-center">
                <h3 className="text-[12px] font-bold text-slate-500 dark:text-slate-400 tracking-wide self-start w-full border-b border-slate-100 dark:border-white/10 pb-4 mb-6">Score Distribution</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={chartData} dataKey="score" nameKey="subject" cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={2} label={renderCustomizedLabel} stroke="none">
                      {chartData.map((entry, index) => <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />)}
                    </Pie>
                    <RechartsTooltip contentStyle={{ borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: '#1e293b', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3)' }} itemStyle={{ fontWeight: '600', letterSpacing: '0.1em', color: '#f8fafc', textTransform: 'uppercase', fontSize: '10px' }} cursor={{fill: 'transparent'}} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md p-8 rounded-[2rem] shadow-sm dark:shadow-none border border-slate-200 dark:border-white/10 flex flex-col items-center">
                <h3 className="text-[12px] font-bold text-slate-500 dark:text-slate-400 tracking-wide self-start w-full border-b border-slate-100 dark:border-white/10 pb-4 mb-6">Skill Radar vs. Average</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <RadarChart cx="50%" cy="50%" outerRadius="65%" data={chartData}>
                    <PolarGrid stroke="rgba(150,150,150,0.2)" />
                    <PolarAngleAxis dataKey="subject" tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em' }} />
                    <PolarRadiusAxis angle={30} domain={[0, 10]} tick={{ fill: '#64748b', fontSize: 9 }} />
                    <Radar name="Campaign Avg" dataKey="average" stroke="#10b981" fill="#10b981" fillOpacity={0.1} strokeDasharray="4 4" />
                    <Radar name="Candidate" dataKey="score" stroke="#4f46e5" fill="#4f46e5" fillOpacity={0.5} />
                    <RechartsTooltip contentStyle={{ borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: '#1e293b', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3)' }} itemStyle={{ fontWeight: '600', letterSpacing: '0.1em', color: '#f8fafc', textTransform: 'uppercase', fontSize: '10px' }} />
                    <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.1em', color: '#94a3b8', paddingTop: '10px' }}/> 
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* TAB CONTENT: QUESTIONS */}
        {activeTab === 'questions' && (
          <div className="space-y-4">
            {report.qa_breakdown.map((qa, idx) => (
              <div key={idx} className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none rounded-[2rem] p-6 flex flex-col md:flex-row gap-6">
                 <div className="flex-1 min-w-0">
                    <h4 className="font-medium dark:font-light text-slate-900 dark:text-white mb-3 text-lg leading-relaxed tracking-wide">
                      <span className="text-indigo-600 dark:text-indigo-400 font-bold mr-2">Q{idx + 1}.</span>{qa.question}
                    </h4>
                    <div className="bg-slate-50 dark:bg-black/50 border border-slate-100 dark:border-white/5 p-4 rounded-xl text-slate-700 dark:text-slate-300 text-sm font-light italic whitespace-pre-wrap leading-relaxed">
                      "{qa.candidate_answer}"
                    </div>
                 </div>
                 
                 <div className="w-full md:w-28 flex flex-col items-center justify-center md:border-l border-t md:border-t-0 border-slate-100 dark:border-white/10 md:pl-6 pt-5 md:pt-0 shrink-0 relative group">
                    <div className="text-[12px] text-slate-500 dark:text-slate-400 font-bold tracking-wide mb-1.5">Score</div>
                    <div className={`text-3xl font-light tracking-tighter ${qa.score_out_of_10 >= 7 ? 'text-emerald-600 dark:text-emerald-400' : qa.score_out_of_10 >= 5 ? 'text-amber-500 dark:text-amber-400' : 'text-rose-500 dark:text-rose-400'}`}>
                      {qa.score_out_of_10}<span className="text-sm text-slate-400 dark:text-slate-500 font-medium">/10</span>
                    </div>
                    <div className="mt-3 text-slate-400 dark:text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-400 cursor-help flex items-center gap-1.5 text-[12px] font-bold tracking-wide transition-colors">
                      <HelpCircle size={12}/> Reasoning
                    </div>
                    <div className="absolute right-0 md:right-full top-full md:top-1/2 md:-translate-y-1/2 mt-2 md:mt-0 md:mr-6 w-64 md:w-80 bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-xs font-light leading-relaxed rounded-2xl p-5 shadow-2xl opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-20 text-left">
                      <div className="font-bold text-[12px] tracking-wide text-slate-400 dark:text-slate-500 mb-2 border-b border-slate-700 dark:border-slate-200 pb-2">AI Reasoning</div>
                      {qa.reasoning}
                    </div>
                 </div>
              </div>
            ))}
          </div>
        )}

        {/* TAB CONTENT: CODING */}
        {activeTab === 'coding' && hasCoding && (
          <div className="space-y-8">
            {report.coding_assessments.map((codeTask, idx) => {
              const parsedAns = parseUserAnswer(codeTask.user_answer);
              return (
              <div key={idx} className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none rounded-[2rem] overflow-hidden">
                 <div className="p-8 border-b border-slate-100 dark:border-white/10 flex flex-col lg:flex-row justify-between items-start gap-8">
                    <div className="flex-1 min-w-0"> 
                      <h4 className="font-light tracking-wide text-slate-900 dark:text-white mb-3 text-2xl flex items-center gap-3">
                         <span className="text-indigo-600 dark:text-indigo-400 font-bold">Task {idx + 1}:</span> Problem Statement
                      </h4>
                      <p className="text-sm font-light leading-relaxed text-slate-600 dark:text-slate-400 whitespace-pre-wrap">{codeTask.question}</p>
                    </div>
                 </div>

                 <div className="p-8 bg-slate-50/50 dark:bg-transparent">
                   {parsedAns.explanation && (
                     <div className="mb-8">
                       <h4 className="text-[12px] font-bold text-slate-500 dark:text-slate-400 tracking-wide mb-3 flex items-center gap-2">
                           <MessageSquare size={14}/> Verbal Explanation
                       </h4>
                       <p className="text-sm font-light text-slate-700 dark:text-slate-300 italic border-l-2 border-indigo-400 pl-5 py-1 leading-relaxed">{parsedAns.explanation}</p>
                     </div>
                   )}
                   <div className="rounded-2xl overflow-hidden border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none">
                     <div className="bg-slate-100 dark:bg-[#111] px-6 py-3 flex justify-between items-center border-b border-slate-200 dark:border-white/5">
                        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300 text-[12px] font-mono font-bold tracking-wide">
                          <Code size={14} className="text-indigo-500 dark:text-indigo-400"/> {parsedAns.language} Solution
                        </div>
                     </div>
                     <div className="p-6 bg-slate-800 dark:bg-black/60 overflow-x-auto">
                       <pre className="text-emerald-400 font-mono text-sm font-light leading-relaxed">{parsedAns.code || "// No code submitted."}</pre>
                     </div>
                   </div>

                   {parsedAns.output && (
                     <div className="mt-6 rounded-2xl overflow-hidden border border-slate-200 dark:border-white/10 bg-white dark:bg-transparent shadow-sm dark:shadow-none">
                        <div className="bg-slate-100 dark:bg-[#111] px-6 py-3 text-[12px] font-bold text-slate-500 dark:text-slate-400 tracking-wide border-b border-slate-200 dark:border-white/5 flex items-center gap-2">
                          <Terminal size={14}/> Console Output
                        </div>
                        <div className="p-6 bg-slate-50 dark:bg-black/40 overflow-x-auto">
                           <pre className="text-slate-600 dark:text-slate-300 font-mono text-xs font-light whitespace-pre-wrap">{parsedAns.output}</pre>
                        </div>
                     </div>
                   )}
                 </div>
              </div>
              );
            })}
          </div>
        )}

        {/* TAB CONTENT: VIDEO ANALYTICS */}
        {activeTab === 'video' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="col-span-1 lg:col-span-2 bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md p-6 lg:p-8 rounded-[2rem] border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none flex flex-col">
              <h3 className="font-medium tracking-wide text-slate-900 dark:text-white text-xl mb-6 flex items-center gap-3">
                <Video size={20} className="text-indigo-600 dark:text-indigo-400" /> Screen Recording
              </h3>
              <div className="w-full aspect-video bg-black rounded-2xl overflow-hidden shadow-inner border border-slate-200 dark:border-white/5">
                <video controls controlsList="nodownload" crossOrigin="use-credentials" className="w-full h-full object-contain" src={videoUrl}>
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>

            <div className="col-span-1 bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md p-6 lg:p-8 rounded-[2rem] border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none flex flex-col">
              <div className="flex items-center justify-between border-b border-slate-100 dark:border-white/10 pb-5 mb-6 shrink-0">
                 <h3 className="font-medium tracking-wide text-slate-900 dark:text-white text-xl flex items-center gap-3">
                   <Flag size={20} className="text-rose-500" /> Timeline & Flags
                 </h3>
                 <span className="bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-slate-400 text-[12px] font-bold tracking-wide px-3 py-1.5 rounded-lg border border-slate-200 dark:border-white/10">AI Beta</span>
              </div>
              <div className="flex-1 overflow-y-auto pr-2 timeline-scrollbar min-h-[300px]">
                <p className="text-sm font-light leading-relaxed text-slate-500 dark:text-slate-400 mb-6 border-l-2 border-indigo-500 pl-4 py-1 bg-indigo-50/50 dark:bg-indigo-500/5 rounded-r-xl">
                  AI Video Analysis is currently pending. Future updates will populate this timeline with automated behavior tracking.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ========================================================= */}
      {/* 🔥 HIDDEN PRINT LAYOUT: Strictly formatted for the PDF export */}
      {/* ========================================================= */}
      <div className="absolute left-[-9999px] top-[-9999px] w-[800px] z-[-1]">
        <div ref={pdfRef} className="bg-white text-slate-900 w-[800px] p-8 font-sans mx-auto">
          
          {/* Header */}
          <div className="border-b-2 border-slate-200 pb-6 mb-8 flex justify-between items-end break-inside-avoid">
            <div>
              <h1 className="text-3xl font-bold tracking-tight mb-3 text-slate-900 break-words whitespace-pre-wrap leading-tight">{data.candidate_name}</h1>
              <p className="text-xs text-indigo-600 tracking-wide font-bold">Interview Assessment Report</p>
            </div>
            <div className="text-right">
              <p className="text-[12px] text-slate-400 tracking-wide font-bold mb-1">Session ID</p>
              <p className="text-[12px] font-mono text-slate-600 bg-slate-100 px-2 py-1 rounded">{data.session_id}</p>
            </div>
          </div>

          {/* Scores Summary */}
          <div className="mb-8 break-inside-avoid">
             <h3 className="text-[12px] font-bold text-slate-400 tracking-wide mb-4">Performance Overview</h3>
             <div className="grid grid-cols-3 gap-4">
                
                {/* Overall Score spanning full width */}
                <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-100 col-span-3 flex justify-between items-center break-inside-avoid">
                   <span className="text-xs font-bold text-indigo-800 tracking-wide">Overall Interview Score</span>
                   <div className="text-3xl font-bold text-indigo-600">{data.overall_score}<span className="text-sm text-indigo-400 font-medium">/10</span></div>
                </div>
                
                {/* Sub Scores carefully centered and balanced */}
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex flex-col items-center justify-center h-full break-inside-avoid text-center">
                   <span className="block text-[12px] font-bold text-slate-500 tracking-wide mb-3 leading-normal break-words whitespace-pre-wrap w-full">{data.skill_names.skill1}</span>
                   <span className="text-2xl font-light text-slate-900 mt-auto">{scores.skill1_score}<span className="text-xs text-slate-400">/10</span></span>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex flex-col items-center justify-center h-full break-inside-avoid text-center">
                   <span className="block text-[12px] font-bold text-slate-500 tracking-wide mb-3 leading-normal break-words whitespace-pre-wrap w-full">{data.skill_names.skill2}</span>
                   <span className="text-2xl font-light text-slate-900 mt-auto">{scores.skill2_score}<span className="text-xs text-slate-400">/10</span></span>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex flex-col items-center justify-center h-full break-inside-avoid text-center">
                   <span className="block text-[12px] font-bold text-slate-500 tracking-wide mb-3 leading-normal break-words whitespace-pre-wrap w-full">{data.skill_names.skill3}</span>
                   <span className="text-2xl font-light text-slate-900 mt-auto">{scores.skill3_score}<span className="text-xs text-slate-400">/10</span></span>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex flex-col items-center justify-center h-full break-inside-avoid text-center">
                   <span className="block text-[12px] font-bold text-slate-500 tracking-wide mb-3 leading-normal break-words whitespace-pre-wrap w-full">Communication</span>
                   <span className="text-2xl font-light text-slate-900 mt-auto">{scores.communication_score}<span className="text-xs text-slate-400">/10</span></span>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex flex-col items-center justify-center h-full break-inside-avoid text-center">
                   <span className="block text-[12px] font-bold text-slate-500 tracking-wide mb-3 leading-normal break-words whitespace-pre-wrap w-full">Problem Solving</span>
                   <span className="text-2xl font-light text-slate-900 mt-auto">{scores.problem_solving_score}<span className="text-xs text-slate-400">/10</span></span>
                </div>
             </div>
          </div>

          {/* Strengths & Weaknesses */}
          {(scores.strengths || scores.weaknesses) && (
            <div className="grid grid-cols-2 gap-6 mb-8 break-inside-avoid">
              <div className="bg-emerald-50/50 p-5 rounded-xl border border-emerald-100">
                <h3 className="text-emerald-800 font-bold text-xs tracking-wide mb-3 flex items-center gap-2"><ThumbsUp size={14}/> Key Strengths</h3>
                {scores.strengths?.length > 0 ? (
                  <ul className="space-y-2">
                    {scores.strengths.map((p, i) => <li key={i} className="text-xs text-slate-700 flex items-start gap-2"><CheckCircle size={12} className="text-emerald-500 shrink-0 mt-0.5"/> <span className="leading-relaxed">{p}</span></li>)}
                  </ul>
                ) : <p className="text-slate-500 text-xs italic">No strengths identified.</p>}
              </div>

              <div className="bg-rose-50/50 p-5 rounded-xl border border-rose-100">
                <h3 className="text-rose-800 font-bold text-xs tracking-wide mb-3 flex items-center gap-2"><AlertCircle size={14}/> Areas for Improvement</h3>
                {scores.weaknesses?.length > 0 ? (
                  <ul className="space-y-2">
                    {scores.weaknesses.map((p, i) => <li key={i} className="text-xs text-slate-700 flex items-start gap-2"><XCircle size={12} className="text-rose-500 shrink-0 mt-0.5"/> <span className="leading-relaxed">{p}</span></li>)}
                  </ul>
                ) : <p className="text-slate-500 text-xs italic">No weaknesses identified.</p>}
              </div>
            </div>
          )}

          {/* AI Summary */}
          <div className="mb-10 break-inside-avoid bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
            <h3 className="text-[12px] font-bold text-slate-400 tracking-wide mb-3 flex items-center gap-2">
              <LayoutDashboard size={14}/> AI Summary & Feedback
            </h3>
            <p className="text-sm text-slate-700 font-light leading-relaxed whitespace-pre-wrap break-words">{scores.overall_description}</p>
          </div>

          {/* 🔥 FORCED PAGE BREAK BEFORE Q&A */}
          <div style={{ pageBreakBefore: 'always' }} className="pt-8">
             <h3 className="text-[12px] font-bold text-slate-400 tracking-wide mb-4 flex items-center gap-2 break-inside-avoid">
               <MessageSquare size={14}/> Q&A Breakdown
             </h3>
             <div className="space-y-6">
               {report.qa_breakdown.map((qa, idx) => (
                  <div key={idx} className="bg-white p-6 rounded-2xl border border-slate-200 break-inside-avoid shadow-sm">
                     <div className="flex justify-between items-start mb-4 gap-6">
                        <h4 className="font-medium text-slate-900 text-sm leading-relaxed break-words whitespace-pre-wrap">
                          <span className="text-indigo-600 font-bold mr-2">Q{idx + 1}.</span>{qa.question}
                        </h4>
                        <div className="font-light text-slate-900 text-xl shrink-0 bg-slate-50 px-3 py-1 rounded-lg border border-slate-100">
                          {qa.score_out_of_10}<span className="text-[12px] text-slate-400 font-bold ml-1">/ 10</span>
                        </div>
                     </div>
                     
                     <div className="mb-4">
                        <span className="font-bold tracking-wide text-[12px] text-slate-400 block mb-2">Candidate Response</span>
                        <div className="text-sm font-light text-slate-700 bg-slate-50 p-4 rounded-xl border border-slate-100 whitespace-pre-wrap break-words">
                           {qa.candidate_answer ? `"${qa.candidate_answer}"` : "No answer provided."}
                        </div>
                     </div>
                     
                     <div>
                        <span className="font-bold tracking-wide text-[12px] text-indigo-500 block mb-2">AI Reasoning</span>
                        <p className="text-sm font-light text-slate-600 leading-relaxed whitespace-pre-wrap break-words pl-3 border-l-2 border-indigo-200">{qa.reasoning}</p>
                     </div>
                  </div>
               ))}
             </div>
          </div>

        </div>
      </div>

    </div>
  );
};

export default Report;